import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Plus, 
  FileText, 
  ClipboardList,
  Calendar,
  Phone,
  Mail,
  User,
  PenLine,
  History,
  Eye,
  Save,
  X,
  Printer,
  Camera,
  Image,
  Upload,
  Lock,
  Shield,
  KeyRound
} from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get field label from data key
const getFieldLabel = (key, templateFields) => {
  // If template_fields exists, try to find the label by matching the key to field.id
  if (templateFields && templateFields.length > 0) {
    const field = templateFields.find(f => f.id === key);
    if (field) return field.label;
  }
  
  // Check if key looks like a UUID (e.g., "field-xxxxx" or UUID format)
  const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  const fieldPattern = /^field-/i;
  
  if (uuidPattern.test(key) || fieldPattern.test(key)) {
    // Return a generic label if we can't find a proper one
    return "Campo";
  }
  
  // Otherwise, the key is already a human-readable label
  return key;
};

// Helper to get display entries from record data
const getDisplayEntries = (record, maxEntries = 3) => {
  if (record.template_fields && record.template_fields.length > 0) {
    // Use template_fields to get labels and values
    return record.template_fields.slice(0, maxEntries).map(field => ({
      label: field.label,
      value: Array.isArray(record.data[field.id]) 
        ? record.data[field.id].join(", ") 
        : (record.data[field.id] || "-")
    }));
  }
  
  // Fallback to data keys (for old records where keys are labels)
  return Object.entries(record.data).slice(0, maxEntries).map(([key, value]) => ({
    label: getFieldLabel(key, record.template_fields),
    value: Array.isArray(value) ? value.join(", ") : (value || "-")
  }));
};

export const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // PIN Protection
  const [pinVerified, setPinVerified] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [checkingPin, setCheckingPin] = useState(true);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [forgotPinPassword, setForgotPinPassword] = useState("");
  const [resettingPin, setResettingPin] = useState(false);
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  
  // Anamnesis
  const [anamnesisRecords, setAnamnesisRecords] = useState([]);
  const [anamnesisTemplates, setAnamnesisTemplates] = useState([]);
  const [newAnamnesisOpen, setNewAnamnesisOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [anamnesisFormData, setAnamnesisFormData] = useState({});
  
  // View/Edit Anamnesis
  const [viewAnamnesisOpen, setViewAnamnesisOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(false);
  const [editRecordData, setEditRecordData] = useState({});
  const [recordHistory, setRecordHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Consent
  const [consentRecords, setConsentRecords] = useState([]);
  const [consentTemplates, setConsentTemplates] = useState([]);
  const [newConsentOpen, setNewConsentOpen] = useState(false);
  const [selectedConsentTemplate, setSelectedConsentTemplate] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  
  // View Consent
  const [viewConsentOpen, setViewConsentOpen] = useState(false);
  const [selectedConsentRecord, setSelectedConsentRecord] = useState(null);

  // Evolution Photos
  const [evolutionPhotos, setEvolutionPhotos] = useState([]);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoForm, setPhotoForm] = useState({ image_data: "", notes: "" });
  const [viewPhotoOpen, setViewPhotoOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [deletePhotoId, setDeletePhotoId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Check if client has PIN on mount
  useEffect(() => {
    checkClientPin();
  }, [clientId]);
  
  // Fetch data only after PIN is verified
  useEffect(() => {
    if (pinVerified) {
      fetchData();
    }
  }, [clientId, pinVerified]);

  const checkClientPin = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/has-pin`);
      setHasPin(response.data.has_pin);
      
      // Check if PIN is locked
      if (response.data.is_locked) {
        setPinError(`Muitas tentativas incorretas. Tente novamente em ${response.data.remaining_minutes} minuto(s).`);
      }
      
      if (!response.data.has_pin) {
        // No PIN, grant access
        setPinVerified(true);
      }
    } catch (error) {
      toast.error("Erro ao verificar acesso do cliente");
      navigate("/clients");
    } finally {
      setCheckingPin(false);
    }
  };

  const verifyPin = async () => {
    if (pinInput.length !== 4) {
      setPinError("Digite os 4 dígitos do PIN");
      return;
    }
    
    try {
      await axios.post(`${API}/clients/${clientId}/verify-pin`, { pin: pinInput });
      setPinVerified(true);
      setPinError("");
    } catch (error) {
      const statusCode = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (statusCode === 429) {
        // Rate limited / Locked
        setPinError(detail || "Muitas tentativas. Aguarde alguns minutos.");
      } else {
        setPinError(detail || "PIN incorreto");
      }
      setPinInput("");
    }
  };

  const handleSetPin = async () => {
    if (newPin.length !== 4 || !newPin.match(/^\d{4}$/)) {
      toast.error("O PIN deve ter exatamente 4 dígitos numéricos");
      return;
    }
    if (newPin !== confirmPin) {
      toast.error("Os PINs não coincidem");
      return;
    }
    
    try {
      await axios.post(`${API}/clients/${clientId}/pin`, { pin: newPin });
      toast.success("PIN definido com sucesso!");
      setShowPinSetup(false);
      setNewPin("");
      setConfirmPin("");
      setHasPin(true);
    } catch (error) {
      toast.error("Erro ao definir PIN");
    }
  };

  const handleRemovePin = async () => {
    try {
      await axios.delete(`${API}/clients/${clientId}/pin`);
      toast.success("PIN removido!");
      setHasPin(false);
    } catch (error) {
      toast.error("Erro ao remover PIN");
    }
  };

  const handleForgotPin = async () => {
    if (!forgotPinPassword) {
      toast.error("Digite sua senha");
      return;
    }
    
    setResettingPin(true);
    try {
      await axios.post(`${API}/clients/${clientId}/reset-pin`, {
        password: forgotPinPassword
      });
      toast.success("PIN removido! Você pode acessar o cliente agora.");
      setShowForgotPin(false);
      setForgotPinPassword("");
      setHasPin(false);
      setPinVerified(true);
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao resetar PIN";
      toast.error(message);
    } finally {
      setResettingPin(false);
    }
  };

  const fetchData = async () => {
    try {
      const [clientRes, anamnesisRes, consentRes, aTemplatesRes, cTemplatesRes, photosRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/clients/${clientId}/anamnesis`),
        axios.get(`${API}/clients/${clientId}/consent`),
        axios.get(`${API}/anamnesis-templates`),
        axios.get(`${API}/consent-templates`),
        axios.get(`${API}/clients/${clientId}/photos`),
      ]);
      
      setClient(clientRes.data);
      setEditData(clientRes.data);
      setAnamnesisRecords(anamnesisRes.data);
      setEvolutionPhotos(photosRes.data);
      setConsentRecords(consentRes.data);
      setAnamnesisTemplates(aTemplatesRes.data);
      setConsentTemplates(cTemplatesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados do cliente");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        birth_date: editData.birth_date,
        notes: editData.notes,
      });
      setClient(editData);
      setEditing(false);
      toast.success("Cliente atualizado!");
    } catch (error) {
      toast.error("Erro ao atualizar cliente");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success("Cliente excluído!");
      navigate("/clients");
    } catch (error) {
      toast.error("Erro ao excluir cliente");
    }
  };

  const handleCreateAnamnesis = async () => {
    if (!selectedTemplate) return;
    
    // Validate required fields
    const requiredFields = selectedTemplate.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(field => {
      const value = anamnesisFormData[field.id];
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return !value || value.toString().trim() === "";
    });
    
    if (missingFields.length > 0) {
      toast.error(`Preencha os campos obrigatórios: ${missingFields.map(f => f.label).join(", ")}`);
      return;
    }
    
    try {
      await axios.post(`${API}/clients/${clientId}/anamnesis`, {
        template_id: selectedTemplate.id,
        data: anamnesisFormData,
      });
      toast.success("Ficha de anamnese criada!");
      setNewAnamnesisOpen(false);
      setSelectedTemplate(null);
      setAnamnesisFormData({});
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar ficha");
    }
  };

  const openAnamnesisRecord = async (record) => {
    setSelectedRecord(record);
    setEditRecordData(record.data || {});
    setEditingRecord(false);
    setShowHistory(false);
    setViewAnamnesisOpen(true);
    
    // Fetch history
    try {
      const response = await axios.get(`${API}/clients/${clientId}/anamnesis/${record.id}/history`);
      setRecordHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
      setRecordHistory([]);
    }
  };

  const handleUpdateAnamnesis = async () => {
    if (!selectedRecord) return;
    
    // Validate required fields if template_fields is available
    if (selectedRecord.template_fields) {
      const requiredFields = selectedRecord.template_fields.filter(f => f.required);
      const missingFields = requiredFields.filter(field => {
        const value = editRecordData[field.id];
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return !value || value.toString().trim() === "";
      });
      
      if (missingFields.length > 0) {
        toast.error(`Preencha os campos obrigatórios: ${missingFields.map(f => f.label).join(", ")}`);
        return;
      }
    }
    
    try {
      await axios.put(`${API}/clients/${clientId}/anamnesis/${selectedRecord.id}`, {
        data: editRecordData,
      });
      toast.success("Ficha atualizada!");
      setEditingRecord(false);
      fetchData();
      
      // Refresh history
      const response = await axios.get(`${API}/clients/${clientId}/anamnesis/${selectedRecord.id}/history`);
      setRecordHistory(response.data);
      
      // Update selected record
      const updatedRecord = await axios.get(`${API}/clients/${clientId}/anamnesis/${selectedRecord.id}`);
      setSelectedRecord(updatedRecord.data);
    } catch (error) {
      toast.error("Erro ao atualizar ficha");
    }
  };

  const handleCreateConsent = async () => {
    if (!selectedConsentTemplate) return;
    
    if (!signatureData) {
      toast.error("Por favor, solicite a assinatura do cliente");
      return;
    }
    
    try {
      await axios.post(`${API}/clients/${clientId}/consent`, {
        template_id: selectedConsentTemplate.id,
        signature_data: signatureData,
      });
      toast.success("Termo de consentimento registrado!");
      setNewConsentOpen(false);
      setSelectedConsentTemplate(null);
      setSignatureData(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao registrar termo");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR");
  };

  // Print Anamnesis Record
  const handlePrintAnamnesis = () => {
    if (!selectedRecord || !client) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ficha de Anamnese - ${client.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { margin: 0; font-size: 24px; }
          .header p { margin: 5px 0; color: #666; }
          .client-info { background: #f5f5f5; padding: 15px; margin-bottom: 30px; border-radius: 5px; }
          .client-info h3 { margin: 0 0 10px 0; }
          .field { margin-bottom: 20px; }
          .field-label { font-weight: bold; color: #333; margin-bottom: 5px; text-transform: uppercase; font-size: 12px; }
          .field-value { padding: 10px; background: #fafafa; border: 1px solid #ddd; min-height: 20px; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
          .meta { font-size: 12px; color: #666; margin-bottom: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>FICHA DE ANAMNESE</h1>
          <p>${selectedRecord.template_name}</p>
        </div>
        
        <div class="client-info">
          <h3>Dados do Cliente</h3>
          <p><strong>Nome:</strong> ${client.name}</p>
          ${client.email ? `<p><strong>Email:</strong> ${client.email}</p>` : ''}
          ${client.phone ? `<p><strong>Telefone:</strong> ${client.phone}</p>` : ''}
          ${client.birth_date ? `<p><strong>Data de Nascimento:</strong> ${formatDate(client.birth_date)}</p>` : ''}
        </div>
        
        <div class="meta">
          <p>Criado em: ${formatDate(selectedRecord.created_at)}</p>
          ${selectedRecord.updated_at ? `<p>Última edição: ${formatDate(selectedRecord.updated_at)}</p>` : ''}
        </div>
        
        ${(selectedRecord.template_fields || []).map(field => `
          <div class="field">
            <div class="field-label">${field.label}</div>
            <div class="field-value">${
              Array.isArray(selectedRecord.data[field.id]) 
                ? selectedRecord.data[field.id].join(", ") 
                : (selectedRecord.data[field.id] || "-")
            }</div>
          </div>
        `).join('')}
        
        <div class="footer">
          <p>Documento gerado em ${new Date().toLocaleString('pt-BR')}</p>
          <p>AESTHA - Plataforma para Esteticistas</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Print Consent Record
  const handlePrintConsent = (record) => {
    if (!record || !client) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Termo de Consentimento - ${client.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { margin: 0; font-size: 24px; }
          .header p { margin: 5px 0; color: #666; }
          .client-info { background: #f5f5f5; padding: 15px; margin-bottom: 30px; border-radius: 5px; }
          .client-info h3 { margin: 0 0 10px 0; }
          .content { margin-bottom: 30px; line-height: 1.6; white-space: pre-wrap; }
          .signature-section { margin-top: 40px; border-top: 1px solid #ddd; padding-top: 20px; }
          .signature-section h4 { margin-bottom: 10px; }
          .signature-img { max-height: 80px; border: 1px solid #ddd; background: white; }
          .signature-date { margin-top: 10px; font-size: 14px; color: #666; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>TERMO DE CONSENTIMENTO</h1>
          <p>${record.template_name}</p>
        </div>
        
        <div class="client-info">
          <h3>Dados do Cliente</h3>
          <p><strong>Nome:</strong> ${client.name}</p>
          ${client.email ? `<p><strong>Email:</strong> ${client.email}</p>` : ''}
          ${client.phone ? `<p><strong>Telefone:</strong> ${client.phone}</p>` : ''}
        </div>
        
        <div class="content">
          ${record.template_content || 'Conteúdo do termo não disponível.'}
        </div>
        
        ${record.signature_data ? `
          <div class="signature-section">
            <h4>Assinatura do Cliente:</h4>
            <img src="${record.signature_data}" alt="Assinatura" class="signature-img" />
            <p class="signature-date">Assinado em: ${formatDate(record.signed_at)}</p>
          </div>
        ` : ''}
        
        <div class="footer">
          <p>Documento gerado em ${new Date().toLocaleString('pt-BR')}</p>
          <p>AESTHA - Plataforma para Esteticistas</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Evolution Photo Functions
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Apenas imagens são permitidas");
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error("Imagem muito grande. Máximo 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPhotoForm({
        ...photoForm,
        image_data: reader.result
      });
    };
    reader.readAsDataURL(file);
  };

  const savePhoto = async () => {
    if (!photoForm.image_data) {
      toast.error("Selecione uma imagem");
      return;
    }

    setUploadingPhoto(true);
    try {
      await axios.post(`${API}/clients/${clientId}/photos`, photoForm);
      toast.success(t("photo_added"));
      setPhotoDialogOpen(false);
      setPhotoForm({ image_data: "", notes: "" });
      // Refresh photos
      const photosRes = await axios.get(`${API}/clients/${clientId}/photos`);
      setEvolutionPhotos(photosRes.data);
    } catch (error) {
      toast.error("Erro ao salvar foto");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openPhotoViewer = async (photo) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/photos/${photo.id}`);
      setSelectedPhoto(response.data);
      setViewPhotoOpen(true);
    } catch (error) {
      toast.error("Erro ao carregar foto");
    }
  };

  const deletePhoto = async () => {
    if (!deletePhotoId) return;

    try {
      await axios.delete(`${API}/clients/${clientId}/photos/${deletePhotoId}`);
      toast.success(t("photo_deleted"));
      setDeletePhotoId(null);
      // Refresh photos
      const photosRes = await axios.get(`${API}/clients/${clientId}/photos`);
      setEvolutionPhotos(photosRes.data);
    } catch (error) {
      toast.error("Erro ao excluir foto");
    }
  };

  const formatPhotoDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const locale = language === "pt" ? "pt-BR" : language === "es" ? "es-ES" : "en-US";
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Show loading while checking PIN
  if (checkingPin) {
    return (
      <Layout>
        <div className="p-6 md:p-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 loading-skeleton" />
            <div className="h-32 loading-skeleton" />
          </div>
        </div>
      </Layout>
    );
  }

  // Show PIN verification screen if PIN is required
  if (hasPin && !pinVerified) {
    return (
      <Layout>
        <div className="p-6 md:p-12 flex items-center justify-center min-h-[60vh]">
          <div className="w-full max-w-sm">
            <div className="bento-item text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Lock size={32} className="text-primary" />
              </div>
              <h2 className="font-serif text-2xl mb-2">Acesso Protegido</h2>
              <p className="text-muted-foreground mb-6">
                Este cliente está protegido por PIN. Digite os 4 dígitos para continuar.
              </p>
              
              <div className="space-y-4">
                <div className="flex justify-center gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <input
                      key={i}
                      type="password"
                      inputMode="numeric"
                      maxLength={1}
                      value={pinInput[i] || ""}
                      className="w-12 h-14 text-center text-2xl font-mono border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        const newPin = pinInput.split("");
                        newPin[i] = val;
                        setPinInput(newPin.join("").slice(0, 4));
                        // Auto focus next input
                        if (val && i < 3) {
                          const next = e.target.nextElementSibling;
                          if (next) next.focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !pinInput[i] && i > 0) {
                          const prev = e.target.previousElementSibling;
                          if (prev) prev.focus();
                        }
                        if (e.key === "Enter" && pinInput.length === 4) {
                          verifyPin();
                        }
                      }}
                      data-testid={`pin-input-${i}`}
                    />
                  ))}
                </div>
                
                {pinError && (
                  <p className="text-sm text-red-500">{pinError}</p>
                )}
                
                <Button 
                  onClick={verifyPin} 
                  className="btn-primary w-full"
                  disabled={pinInput.length !== 4}
                  data-testid="verify-pin-button"
                >
                  Verificar PIN
                </Button>
                
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotPin(true)}
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-pin-link"
                  >
                    Esqueci meu PIN
                  </button>
                  <button
                    onClick={() => navigate("/clients")}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    ← Voltar para clientes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Forgot PIN Dialog */}
        <Dialog open={showForgotPin} onOpenChange={(open) => {
          setShowForgotPin(open);
          if (!open) setForgotPinPassword("");
        }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <KeyRound size={20} />
                Esqueci meu PIN
              </DialogTitle>
              <DialogDescription>
                Para remover o PIN deste cliente, confirme sua identidade digitando a senha da sua conta.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium">Sua senha da conta</Label>
                <Input
                  type="password"
                  value={forgotPinPassword}
                  onChange={(e) => setForgotPinPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  className="mt-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && forgotPinPassword) {
                      handleForgotPin();
                    }
                  }}
                  data-testid="forgot-pin-password"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Atenção:</strong> Isso vai remover o PIN atual. Após acessar o cliente, você poderá definir um novo PIN.
              </div>
              
              <Button 
                onClick={handleForgotPin}
                className="btn-primary w-full"
                disabled={!forgotPinPassword || resettingPin}
                data-testid="confirm-forgot-pin"
              >
                {resettingPin ? "Verificando..." : "Confirmar e Remover PIN"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 loading-skeleton" />
            <div className="h-32 loading-skeleton" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!client) return null;

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="client-detail-page">
        {/* Header */}
        <button
          onClick={() => navigate("/clients")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
          data-testid="back-button"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        {/* Client Info Card */}
        <div className="bento-item mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 border flex items-center justify-center bg-secondary shrink-0">
                <span className="font-serif text-xl sm:text-2xl">
                  {client.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                {editing ? (
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="input-paper text-xl sm:text-2xl font-serif"
                    data-testid="edit-name"
                  />
                ) : (
                  <h1 className="font-serif text-xl sm:text-2xl md:text-3xl truncate">{client.name}</h1>
                )}
                <p className="text-xs sm:text-sm text-muted-foreground font-mono">
                  Cliente desde {formatDate(client.created_at)}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2 shrink-0">
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => setEditing(false)} className="btn-secondary flex-1 sm:flex-none">
                    Cancelar
                  </Button>
                  <Button onClick={handleSave} className="btn-primary flex-1 sm:flex-none" data-testid="save-button">
                    Salvar
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPinSetup(true)} 
                    title={hasPin ? "Gerenciar PIN" : "Definir PIN"}
                    data-testid="pin-button"
                    size="sm"
                    className="h-9 w-9 sm:h-10 sm:w-10 p-0"
                  >
                    {hasPin ? <Shield size={16} className="text-green-600" /> : <KeyRound size={16} />}
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-button" size="sm" className="h-9 w-9 sm:h-10 sm:w-10 p-0">
                    <Edit size={16} />
                  </Button>
                  <Button variant="outline" onClick={() => setDeleteConfirm(true)} data-testid="delete-button" size="sm" className="h-9 w-9 sm:h-10 sm:w-10 p-0">
                    <Trash2 size={16} />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <Phone size={16} className="text-muted-foreground" />
              {editing ? (
                <Input
                  value={editData.phone || ""}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  className="input-paper h-10"
                  placeholder="Telefone"
                />
              ) : (
                <span>{client.phone || "-"}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-muted-foreground" />
              {editing ? (
                <Input
                  value={editData.email || ""}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  className="input-paper h-10"
                  placeholder="Email"
                />
              ) : (
                <span>{client.email || "-"}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-muted-foreground" />
              {editing ? (
                <Input
                  type="date"
                  value={editData.birth_date || ""}
                  onChange={(e) => setEditData({ ...editData, birth_date: e.target.value })}
                  className="input-paper h-10"
                />
              ) : (
                <span>{client.birth_date ? formatDate(client.birth_date) : "-"}</span>
              )}
            </div>
          </div>

          {/* Notes */}
          {(editing || client.notes) && (
            <div className="mt-6 pt-6 border-t">
              <Label className="label-mono">Observações</Label>
              {editing ? (
                <Textarea
                  value={editData.notes || ""}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  rows={3}
                />
              ) : (
                <p className="text-muted-foreground">{client.notes}</p>
              )}
            </div>
          )}
        </div>

        {/* Tabs for Anamnesis, Consent and Evolution */}
        <Tabs defaultValue="anamnesis" className="w-full">
          <TabsList className="w-full flex mb-6 overflow-x-auto scrollbar-hide">
            <TabsTrigger 
              value="anamnesis" 
              className="flex-1 min-w-0 font-mono uppercase text-[10px] sm:text-xs tracking-wider sm:tracking-widest px-2 sm:px-4 whitespace-nowrap" 
              data-testid="anamnesis-tab"
            >
              <span className="hidden sm:inline">Fichas de Anamnese</span>
              <span className="sm:hidden">Anamnese</span>
            </TabsTrigger>
            <TabsTrigger 
              value="consent" 
              className="flex-1 min-w-0 font-mono uppercase text-[10px] sm:text-xs tracking-wider sm:tracking-widest px-2 sm:px-4 whitespace-nowrap" 
              data-testid="consent-tab"
            >
              <span className="hidden sm:inline">Termos de Consentimento</span>
              <span className="sm:hidden">Termos</span>
            </TabsTrigger>
            <TabsTrigger 
              value="evolution" 
              className="flex-1 min-w-0 font-mono uppercase text-[10px] sm:text-xs tracking-wider sm:tracking-widest px-2 sm:px-4 whitespace-nowrap" 
              data-testid="evolution-tab"
            >
              <span className="hidden sm:inline">{t("evolution")}</span>
              <span className="sm:hidden">Evolução</span>
            </TabsTrigger>
          </TabsList>

          {/* Anamnesis Tab */}
          <TabsContent value="anamnesis">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl">Fichas de Anamnese</h2>
              <Dialog open={newAnamnesisOpen} onOpenChange={setNewAnamnesisOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="new-anamnesis-button">
                    <Plus size={16} className="mr-2" />
                    Criar ficha
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">Nova Ficha de Anamnese</DialogTitle>
                  </DialogHeader>
                  
                  {!selectedTemplate ? (
                    <div className="space-y-4 py-4">
                      <Label className="label-mono">Selecione o modelo</Label>
                      {anamnesisTemplates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => setSelectedTemplate(template)}
                          className="w-full text-left bento-item"
                          data-testid={`template-${template.id}`}
                        >
                          <h4 className="font-serif text-lg">{template.name}</h4>
                          {template.description && (
                            <p className="text-sm text-muted-foreground">{template.description}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4 py-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-serif text-lg">{selectedTemplate.name}</h4>
                        <Button variant="ghost" onClick={() => setSelectedTemplate(null)}>
                          Trocar modelo
                        </Button>
                      </div>
                      
                      {selectedTemplate.fields.map((field) => (
                        <div key={field.id}>
                          <Label className="label-mono">
                            {field.label} {field.required && "*"}
                          </Label>
                          {field.field_type === "textarea" ? (
                            <Textarea
                              value={anamnesisFormData[field.id] || ""}
                              onChange={(e) => setAnamnesisFormData({
                                ...anamnesisFormData,
                                [field.id]: e.target.value
                              })}
                              rows={3}
                              required={field.required}
                            />
                          ) : field.field_type === "select" ? (
                            <Select
                              value={anamnesisFormData[field.id] || ""}
                              onValueChange={(value) => setAnamnesisFormData({
                                ...anamnesisFormData,
                                [field.id]: value
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione..." />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options?.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : field.field_type === "checkbox" ? (
                            <div className="space-y-2 mt-2">
                              {field.options?.map((option) => (
                                <div key={option} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`${field.id}-${option}`}
                                    checked={(anamnesisFormData[field.id] || []).includes(option)}
                                    onCheckedChange={(checked) => {
                                      const currentValues = anamnesisFormData[field.id] || [];
                                      const newValues = checked
                                        ? [...currentValues, option]
                                        : currentValues.filter(v => v !== option);
                                      setAnamnesisFormData({
                                        ...anamnesisFormData,
                                        [field.id]: newValues
                                      });
                                    }}
                                  />
                                  <label htmlFor={`${field.id}-${option}`} className="text-sm cursor-pointer">
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Input
                              value={anamnesisFormData[field.id] || ""}
                              onChange={(e) => setAnamnesisFormData({
                                ...anamnesisFormData,
                                [field.id]: e.target.value
                              })}
                              className="input-paper"
                              required={field.required}
                            />
                          )}
                        </div>
                      ))}
                      
                      <Button 
                        onClick={handleCreateAnamnesis} 
                        className="btn-primary w-full"
                        data-testid="save-anamnesis"
                      >
                        Salvar Ficha
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {anamnesisRecords.length > 0 ? (
              <div className="space-y-4">
                {anamnesisRecords.map((record) => (
                  <div 
                    key={record.id} 
                    className="bento-item" 
                    data-testid={`anamnesis-record-${record.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <ClipboardList size={20} strokeWidth={1} />
                        <div>
                          <h4 className="font-serif">{record.template_name}</h4>
                          <p className="text-xs text-muted-foreground font-mono">
                            Criado em {formatDate(record.created_at)}
                            {record.updated_at && ` • Editado em ${formatDate(record.updated_at)}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAnamnesisRecord(record)}
                        data-testid={`open-record-${record.id}`}
                      >
                        <Eye size={14} className="mr-1" />
                        Visualizar
                      </Button>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t grid gap-2">
                      {getDisplayEntries(record, 3).map((entry, idx) => (
                        <p key={idx} className="text-sm">
                          <span className="text-muted-foreground font-medium">{entry.label}:</span>{" "}
                          {entry.value}
                        </p>
                      ))}
                      {(record.template_fields?.length > 3 || Object.keys(record.data).length > 3) && (
                        <p className="text-xs text-muted-foreground">
                          +{Math.max((record.template_fields?.length || Object.keys(record.data).length) - 3, 0)} campos
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state border py-12">
                <ClipboardList className="empty-state-icon" />
                <p className="empty-state-title">Nenhuma ficha criada ainda</p>
                <p className="empty-state-description">
                  Crie sua primeira ficha para manter seus registros profissionais organizados.
                </p>
              </div>
            )}
          </TabsContent>

          {/* Consent Tab */}
          <TabsContent value="consent">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl">Termos de Consentimento</h2>
              <Dialog open={newConsentOpen} onOpenChange={setNewConsentOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="new-consent-button">
                    <Plus size={16} className="mr-2" />
                    Criar termo
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">Registrar Termo de Consentimento</DialogTitle>
                  </DialogHeader>
                  
                  {!selectedConsentTemplate ? (
                    <div className="space-y-4 py-4">
                      <Label className="label-mono">Selecione o termo</Label>
                      {consentTemplates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => setSelectedConsentTemplate(template)}
                          className="w-full text-left bento-item"
                          data-testid={`consent-template-${template.id}`}
                        >
                          <h4 className="font-serif text-lg">{template.name}</h4>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4 py-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-serif text-lg">{selectedConsentTemplate.name}</h4>
                        <Button variant="ghost" onClick={() => {
                          setSelectedConsentTemplate(null);
                          setSignatureData(null);
                        }}>
                          Trocar termo
                        </Button>
                      </div>
                      
                      <div className="border p-4 bg-secondary/50 max-h-48 overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-sm font-sans">
                          {selectedConsentTemplate.content}
                        </pre>
                      </div>
                      
                      <SignaturePad 
                        onSignatureChange={setSignatureData}
                      />
                      
                      <p className="text-sm text-muted-foreground">
                        O cliente deve assinar acima para confirmar que leu e concordou com este termo.
                      </p>
                      
                      <Button 
                        onClick={handleCreateConsent} 
                        className="btn-primary w-full"
                        disabled={!signatureData}
                        data-testid="save-consent"
                      >
                        <PenLine size={16} className="mr-2" />
                        Confirmar Assinatura
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {consentRecords.length > 0 ? (
              <div className="space-y-4">
                {consentRecords.map((record) => (
                  <div key={record.id} className="bento-item" data-testid={`consent-record-${record.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <FileText size={20} strokeWidth={1} />
                        <div>
                          <h4 className="font-serif">{record.template_name}</h4>
                          <p className="text-xs text-muted-foreground font-mono">
                            Assinado em {formatDate(record.signed_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintConsent(record)}
                          title="Imprimir"
                        >
                          <Printer size={14} />
                        </Button>
                        <span className="text-xs font-mono uppercase tracking-widest text-green-600">
                          Assinado
                        </span>
                      </div>
                    </div>
                    {record.signature_data && (
                      <div className="border-t pt-3">
                        <p className="text-xs text-muted-foreground mb-2 font-mono uppercase tracking-widest">Assinatura:</p>
                        <img 
                          src={record.signature_data} 
                          alt="Assinatura" 
                          className="h-16 object-contain bg-white border"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state border py-12">
                <FileText className="empty-state-icon" />
                <p className="empty-state-title">Nenhum termo registrado ainda</p>
                <p className="empty-state-description">
                  Crie um termo para coletar assinaturas com mais segurança.
                </p>
              </div>
            )}
          </TabsContent>

          {/* Evolution Photos Tab */}
          <TabsContent value="evolution">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl">{t("evolution_photos")}</h2>
              <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="new-photo-button">
                    <Plus size={16} className="mr-2" />
                    Adicionar foto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">{t("add_photo")}</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div>
                      <Label className="label-mono">{t("select_image")} *</Label>
                      <div className="border-2 border-dashed p-6 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoSelect}
                          className="hidden"
                          id="photo-upload"
                        />
                        <label htmlFor="photo-upload" className="cursor-pointer">
                          {photoForm.image_data ? (
                            <img 
                              src={photoForm.image_data} 
                              alt="Preview" 
                              className="max-h-48 mx-auto object-contain"
                            />
                          ) : (
                            <div className="space-y-2">
                              <Camera size={32} className="mx-auto text-muted-foreground" />
                              <p className="text-sm text-muted-foreground">
                                Clique para selecionar uma imagem (máx. 5MB)
                              </p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label className="label-mono">{t("photo_notes")}</Label>
                      <Textarea
                        value={photoForm.notes}
                        onChange={(e) => setPhotoForm({ ...photoForm, notes: e.target.value })}
                        placeholder={t("photo_notes_placeholder")}
                        rows={3}
                      />
                    </div>

                    <Button 
                      onClick={savePhoto} 
                      className="btn-primary w-full"
                      disabled={!photoForm.image_data || uploadingPhoto}
                    >
                      {uploadingPhoto ? "Enviando..." : t("upload_image")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {evolutionPhotos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {evolutionPhotos.map((photo) => (
                  <div 
                    key={photo.id} 
                    className="bento-item p-3 cursor-pointer hover:border-foreground transition-colors"
                    onClick={() => openPhotoViewer(photo)}
                    data-testid={`photo-${photo.id}`}
                  >
                    <div className="aspect-square bg-secondary flex items-center justify-center mb-2 overflow-hidden">
                      <Image size={32} className="text-muted-foreground" />
                    </div>
                    <p className="text-xs font-mono text-muted-foreground">
                      {formatPhotoDate(photo.created_at)}
                    </p>
                    {photo.notes && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {photo.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state border py-12">
                <Camera className="empty-state-icon" />
                <p className="empty-state-title">Nenhuma foto registrada ainda</p>
                <p className="empty-state-description">
                  Adicione a primeira foto para acompanhar o progresso do cliente.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Photo Viewer Dialog */}
        <Dialog open={viewPhotoOpen} onOpenChange={setViewPhotoOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-serif text-xl">
                  {selectedPhoto && formatPhotoDate(selectedPhoto.created_at)}
                </DialogTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDeletePhotoId(selectedPhoto?.id);
                    setViewPhotoOpen(false);
                  }}
                >
                  <Trash2 size={14} className="mr-1" />
                  {t("delete")}
                </Button>
              </div>
            </DialogHeader>
            
            {selectedPhoto && (
              <div className="space-y-4">
                <img 
                  src={selectedPhoto.image_data} 
                  alt="Foto de evolução" 
                  className="w-full object-contain max-h-[60vh]"
                />
                {selectedPhoto.notes && (
                  <div className="border-t pt-4">
                    <Label className="label-mono">{t("photo_notes")}</Label>
                    <p className="text-muted-foreground mt-1">{selectedPhoto.notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Photo Confirmation */}
        <AlertDialog open={!!deletePhotoId} onOpenChange={() => setDeletePhotoId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl">
                {t("delete_photo")}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("confirm_delete_photo")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={deletePhoto}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl">
                Excluir cliente?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. Todas as fichas de anamnese e termos de consentimento serão excluídos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* PIN Setup Dialog */}
        <Dialog open={showPinSetup} onOpenChange={setShowPinSetup}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <Shield size={20} />
                {hasPin ? "Gerenciar PIN" : "Definir PIN"}
              </DialogTitle>
              <DialogDescription>
                {hasPin 
                  ? "Este cliente está protegido por PIN. Você pode remover ou alterar o PIN."
                  : "Adicione um PIN de 4 dígitos para proteger o acesso às informações deste cliente."
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {hasPin ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 flex items-center gap-2">
                      <Lock size={16} />
                      Este cliente está protegido por PIN
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={handleRemovePin}
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="remove-pin-button"
                  >
                    Remover PIN
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Novo PIN (4 dígitos)</Label>
                    <Input
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      value={newPin}
                      onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="••••"
                      className="mt-1 text-center text-xl tracking-widest"
                      data-testid="new-pin-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Confirmar PIN</Label>
                    <Input
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="••••"
                      className="mt-1 text-center text-xl tracking-widest"
                      data-testid="confirm-pin-input"
                    />
                  </div>
                  <Button 
                    onClick={handleSetPin}
                    className="btn-primary w-full"
                    disabled={newPin.length !== 4 || confirmPin.length !== 4}
                    data-testid="set-pin-button"
                  >
                    Definir PIN
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* View/Edit Anamnesis Dialog */}
        <Dialog open={viewAnamnesisOpen} onOpenChange={(open) => {
          setViewAnamnesisOpen(open);
          if (!open) {
            setSelectedRecord(null);
            setEditingRecord(false);
            setShowHistory(false);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-serif text-xl">
                  {selectedRecord?.template_name}
                </DialogTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePrintAnamnesis}
                    title="Imprimir"
                  >
                    <Printer size={16} className="mr-1" />
                    Imprimir
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistory(!showHistory)}
                    className={showHistory ? "bg-secondary" : ""}
                  >
                    <History size={16} className="mr-1" />
                    Histórico
                  </Button>
                  {!editingRecord ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingRecord(true)}
                    >
                      <Edit size={16} className="mr-1" />
                      Editar
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingRecord(false);
                        setEditRecordData(selectedRecord?.data || {});
                      }}
                    >
                      <X size={16} className="mr-1" />
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            </DialogHeader>

            {selectedRecord && !showHistory && (
              <div className="space-y-4 py-4">
                {/* Record metadata */}
                <div className="text-xs font-mono text-muted-foreground border-b pb-4">
                  <p>Criado em: {formatDate(selectedRecord.created_at)}</p>
                  {selectedRecord.updated_at && (
                    <p>Última edição: {formatDate(selectedRecord.updated_at)}</p>
                  )}
                </div>

                {/* Fields */}
                {(selectedRecord.template_fields || []).map((field) => (
                  <div key={field.id} className="space-y-1">
                    <Label className="label-mono">{field.label}</Label>
                    
                    {editingRecord ? (
                      // Edit mode
                      field.field_type === "textarea" ? (
                        <Textarea
                          value={editRecordData[field.id] || ""}
                          onChange={(e) => setEditRecordData({
                            ...editRecordData,
                            [field.id]: e.target.value
                          })}
                          rows={3}
                        />
                      ) : field.field_type === "select" ? (
                        <Select
                          value={editRecordData[field.id] || ""}
                          onValueChange={(value) => setEditRecordData({
                            ...editRecordData,
                            [field.id]: value
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options?.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.field_type === "checkbox" ? (
                        <div className="space-y-2">
                          {field.options?.map((option) => (
                            <div key={option} className="flex items-center gap-2">
                              <Checkbox
                                id={`edit-${field.id}-${option}`}
                                checked={(editRecordData[field.id] || []).includes(option)}
                                onCheckedChange={(checked) => {
                                  const currentValues = editRecordData[field.id] || [];
                                  const newValues = checked
                                    ? [...currentValues, option]
                                    : currentValues.filter(v => v !== option);
                                  setEditRecordData({
                                    ...editRecordData,
                                    [field.id]: newValues
                                  });
                                }}
                              />
                              <label htmlFor={`edit-${field.id}-${option}`} className="text-sm cursor-pointer">
                                {option}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Input
                          value={editRecordData[field.id] || ""}
                          onChange={(e) => setEditRecordData({
                            ...editRecordData,
                            [field.id]: e.target.value
                          })}
                        />
                      )
                    ) : (
                      // View mode
                      <p className="text-sm py-2 px-3 bg-secondary/50 rounded min-h-[40px]">
                        {Array.isArray(selectedRecord.data[field.id]) 
                          ? selectedRecord.data[field.id].join(", ") 
                          : (selectedRecord.data[field.id] || "-")}
                      </p>
                    )}
                  </div>
                ))}

                {/* If no template_fields, show raw data */}
                {(!selectedRecord.template_fields || selectedRecord.template_fields.length === 0) && (
                  Object.entries(selectedRecord.data || {}).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <Label className="label-mono">{key}</Label>
                      {editingRecord ? (
                        <Input
                          value={editRecordData[key] || ""}
                          onChange={(e) => setEditRecordData({
                            ...editRecordData,
                            [key]: e.target.value
                          })}
                        />
                      ) : (
                        <p className="text-sm py-2 px-3 bg-secondary/50 rounded min-h-[40px]">
                          {Array.isArray(value) ? value.join(", ") : (value || "-")}
                        </p>
                      )}
                    </div>
                  ))
                )}

                {editingRecord && (
                  <Button 
                    onClick={handleUpdateAnamnesis} 
                    className="btn-primary w-full"
                  >
                    <Save size={16} className="mr-2" />
                    Salvar Alterações
                  </Button>
                )}
              </div>
            )}

            {/* History View */}
            {showHistory && (
              <div className="space-y-4 py-4">
                <h4 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Histórico de Alterações
                </h4>
                
                {recordHistory.length > 0 ? (
                  <div className="space-y-4">
                    {recordHistory.map((entry, index) => (
                      <div key={entry.id} className="border-l-2 border-muted pl-4 pb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-mono uppercase tracking-widest ${
                            entry.action === "created" ? "text-green-600" : "text-blue-600"
                          }`}>
                            {entry.action === "created" ? "Criado" : "Editado"}
                          </span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-2">
                          Por: {entry.user_name}
                        </p>
                        
                        {entry.action === "updated" && entry.changes && Object.keys(entry.changes).length > 0 && (
                          <div className="bg-secondary/50 p-3 rounded text-sm space-y-2">
                            <p className="font-mono text-xs text-muted-foreground">Alterações:</p>
                            {Object.entries(entry.changes).map(([fieldId, change]) => (
                              <div key={fieldId} className="border-b border-muted pb-2 last:border-0 last:pb-0">
                                <p className="font-medium text-xs mb-1">{fieldId}:</p>
                                <p className="text-red-600 line-through text-xs">
                                  {Array.isArray(change.old) ? change.old.join(", ") : (change.old || "(vazio)")}
                                </p>
                                <p className="text-green-600 text-xs">
                                  {Array.isArray(change.new) ? change.new.join(", ") : (change.new || "(vazio)")}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Nenhum histórico disponível
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClientDetail;
